import { DistanceStatCard } from "./DistanceStatCard";
import { DaysActiveRing } from "./DaysActiveRing";
import { PaceChartCard } from "./PaceChartCard";
import { StatMiniCard } from "./StatMiniCard";

const DEFAULT_AVATAR =
  "https://api.dicebear.com/7.x/avataaars/svg?seed=default";

/**
 * Format number with commas (e.g., 6420 -> 6,420)
 */
function formatNumber(num: number): string {
  return num.toLocaleString();
}

interface PerformanceCardProps {
  userAvatar: string;
  isWinner: boolean;
  daysCompleted: number;
  requiredDays: number;
  totalDistance: number;
  totalDays: number;
  averagePace: string | null;
  longestRun: number;
  totalCalories: number;
}

export function PerformanceCard({
  userAvatar,
  isWinner,
  daysCompleted,
  requiredDays: _requiredDays,
  totalDistance,
  totalDays,
  averagePace,
  longestRun,
  totalCalories,
}: PerformanceCardProps) {
  return (
    <div className="px-4">
      <div className="rounded-[2rem] performance-card-outer p-1 border border-primary/20 shadow-2xl shadow-primary/5">
        <div className="rounded-[1.8rem] bg-[#0c1c0c]/80 p-5 backdrop-blur-sm">
          {/* Header */}
          <div className="flex items-center justify-between mb-6">
            <div className="flex items-center gap-3">
              {/* Avatar with glow - more subtle for losers */}
              <div className="relative">
                <div
                  className={`absolute inset-0 rounded-full bg-primary blur-[2px] ${
                    isWinner ? "opacity-40" : "opacity-20"
                  }`}
                />
                <img
                  className="relative size-10 rounded-full object-cover border border-white/20"
                  src={userAvatar || DEFAULT_AVATAR}
                  alt="Your profile"
                />
              </div>
              <div>
                <p className="font-bold text-lg text-white leading-none">
                  {isWinner ? "Performance" : "Your Stats"}
                </p>
                <p
                  className={`text-xs font-medium mt-1 ${
                    isWinner ? "text-primary" : "text-white/50"
                  }`}
                >
                  {isWinner ? "ALL GOALS MET" : "CHALLENGE TOTALS"}
                </p>
              </div>
            </div>
            <div
              className={`px-2 py-1 rounded-lg ${
                isWinner
                  ? "bg-primary/10 border border-primary/20"
                  : "bg-white/5 border border-white/10"
              }`}
            >
              <span
                className={`material-symbols-outlined text-xl ${
                  isWinner ? "text-primary" : "text-white/60"
                }`}
              >
                {isWinner ? "insights" : "bar_chart"}
              </span>
            </div>
          </div>

          {/* Stats Grid */}
          <div className="space-y-3">
            {/* Row 1: Distance + Days Ring */}
            <div className="grid grid-cols-2 gap-3">
              <DistanceStatCard totalDistance={totalDistance} />
              <DaysActiveRing
                daysCompleted={daysCompleted}
                totalDays={totalDays}
                isWinner={isWinner}
              />
            </div>

            {/* Average Pace Card with Chart */}
            <PaceChartCard averagePace={averagePace} />

            {/* Row 3: Longest Run + Calories */}
            <div className="grid grid-cols-2 gap-3">
              <StatMiniCard
                icon="timer"
                label="Longest Run"
                value={longestRun.toFixed(1)}
                unit="km"
              />
              <StatMiniCard
                icon="local_fire_department"
                label="Calories"
                value={formatNumber(totalCalories)}
                unit="kcal"
              />
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}
